// C. Legal documents: versions, approval, publication, re-acceptance.
//
// A version freezes when it leaves draft and publishing retires rather than
// overwrites, so the screen shows the whole history and never offers an
// "edit published" affordance that the backend would refuse anyway.
import { useCallback, useEffect, useState } from "react";
import { FileText, Plus } from "lucide-react";
import { toast } from "sonner";

import {
  CAP, can, fmtDate, humanise, legalError, plGet, plPost,
} from "../../lib/platformLegal";
import {
  Button, Empty, Field, Loading, Modal, Note, Section, Table, TonePill, inputClass,
} from "./ui";

const STATUS_TONES = {
  DRAFT: "border-slate-200 bg-slate-50 text-slate-700",
  INTERNAL_REVIEW: "border-sky-200 bg-sky-50 text-sky-800",
  LEGAL_REVIEW_REQUIRED: "border-amber-200 bg-amber-50 text-amber-900",
  APPROVED: "border-violet-200 bg-violet-50 text-violet-800",
  PUBLISHED: "border-emerald-200 bg-emerald-50 text-emerald-800",
  RETIRED: "border-slate-200 bg-slate-50 text-slate-500",
};

const REVIEW_LABELS = {
  not_reviewed: "Not reviewed by counsel",
  in_review: "With counsel",
  reviewed_by_counsel: "Reviewed by counsel",
  changes_requested: "Changes requested",
};

export default function DocumentsSection({ capabilities }) {
  const [families, setFamilies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [versions, setVersions] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState(false);
  const writable = can(capabilities, CAP.documents);
  const publishable = can(capabilities, CAP.publish);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [docs, reacceptance] = await Promise.all([
        plGet("/documents"), plGet("/reacceptance"),
      ]);
      setFamilies(docs.documents);
      setCampaigns(reacceptance.campaigns);
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openFamily = async (docType) => {
    setSelected(docType);
    try {
      setVersions((await plGet(`/documents/${docType}/versions`)).versions);
    } catch (err) {
      toast.error(legalError(err));
    }
  };

  const transition = async (versionId, toStatus, reason) => {
    try {
      await plPost(`/documents/versions/${versionId}/transition`,
                   { to_status: toStatus, reason });
      toast.success(`Moved to ${humanise(toStatus)}.`);
      await openFamily(selected);
      await load();
    } catch (err) {
      toast.error(legalError(err));
    }
  };

  if (loading) return <Loading label="Reading the legal stack..." />;

  return (
    <div className="space-y-4">
      <Section testId="pl-documents"
               title="Legal document stack"
               description="A version freezes when it leaves draft and publishing retires the
                            previous version rather than overwriting it, so what somebody accepted
                            stays answerable.">
        <Table head={["Document", "Audience", "Published", "Acceptance"]} testId="pl-doc-table"
               minWidth={600}>
          {families.map((family) => (
            <tr key={family.doc_type} data-testid={`pl-doc-${family.doc_type}`}>
              <td className="py-2 pr-3">
                <button onClick={() => openFamily(family.doc_type)}
                        className="text-left font-medium text-slate-900 hover:underline">
                  {family.title}
                </button>
                <div className="text-xs text-slate-500">{family.summary}</div>
              </td>
              <td className="py-2 pr-3 text-xs text-slate-600">{humanise(family.audience)}</td>
              <td className="py-2 pr-3">
                {family.published ? (
                  <TonePill tone={STATUS_TONES.PUBLISHED}>
                    v{family.current_version_number}
                  </TonePill>
                ) : (
                  <TonePill tone="border-amber-200 bg-amber-50 text-amber-900">
                    Not published
                  </TonePill>
                )}
              </td>
              <td className="py-2 pr-3 text-xs text-slate-600">
                {family.acceptance_required ? "Required" : "Informational"}
              </td>
            </tr>
          ))}
        </Table>
        <Note tone="slate">
          Nothing here is marked as reviewed by a lawyer unless a person recorded who reviewed it.
          ConfirmPH has no counsel on record.
        </Note>
      </Section>

      {selected && (
        <Section testId="pl-doc-versions"
                 title={`Versions: ${humanise(selected)}`}
                 actions={writable && (
                   <Button onClick={() => setDrafting(true)} data-testid="pl-new-version">
                     <Plus className="w-4 h-4" /> New version
                   </Button>
                 )}>
          {versions.length === 0 ? (
            <Empty>No versions yet.</Empty>
          ) : (
            <Table head={["Version", "Status", "Counsel review", "Effective", "Actions"]}
                   testId="pl-version-table" minWidth={680}>
              {versions.map((version) => (
                <tr key={version.id} data-testid={`pl-version-${version.version_number}`}>
                  <td className="py-2 pr-3">
                    <div className="font-medium text-slate-900">v{version.version_number}</div>
                    <div className="text-xs text-slate-500">{version.title}</div>
                    <div className="text-[11px] text-slate-400 font-mono break-all">
                      {version.content_hash?.slice(0, 16)}…
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    <TonePill tone={STATUS_TONES[version.status]}>
                      {humanise(version.status)}
                    </TonePill>
                  </td>
                  <td className="py-2 pr-3 text-xs text-slate-600">
                    {REVIEW_LABELS[version.legal_review_status]}
                    {version.legal_reviewer && (
                      <div className="text-slate-500">{version.legal_reviewer}</div>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs text-slate-600">
                    {fmtDate(version.effective_date)}
                    {version.retired_at && (
                      <div className="text-slate-400">Retired {fmtDate(version.retired_at)}</div>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap gap-1.5">
                      {writable && version.status === "DRAFT" && (
                        <Button variant="secondary"
                                onClick={() => transition(version.id, "INTERNAL_REVIEW")}>
                          Send to review
                        </Button>
                      )}
                      {writable && version.status === "INTERNAL_REVIEW" && (
                        <Button variant="secondary"
                                onClick={() => {
                                  const reason = window.prompt(
                                    "What was approved, and on what basis?");
                                  if (reason) transition(version.id, "APPROVED", reason);
                                }}>
                          Approve
                        </Button>
                      )}
                      {publishable && version.status === "APPROVED" && (
                        <Button onClick={() => {
                          const reason = window.prompt("Reason for publishing (optional)") || "";
                          transition(version.id, "PUBLISHED", reason);
                        }} data-testid={`pl-publish-${version.version_number}`}>
                          Publish
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Section>
      )}

      <Section testId="pl-reacceptance"
               title="Re-acceptance campaigns"
               description="A material change can require people to accept again. It never logs
                            anyone out: it prompts, with a grace period.">
        {campaigns.length === 0 ? (
          <Empty testId="pl-reacceptance-empty">No re-acceptance is currently required.</Empty>
        ) : (
          <Table head={["Document", "Version", "Status", "Effective", "Grace until"]}
                 testId="pl-campaign-table">
            {campaigns.map((campaign) => (
              <tr key={campaign.id}>
                <td className="py-2 pr-3">{humanise(campaign.doc_type)}</td>
                <td className="py-2 pr-3">v{campaign.document_version_number}</td>
                <td className="py-2 pr-3">{humanise(campaign.status)}</td>
                <td className="py-2 pr-3">{fmtDate(campaign.effective_date)}</td>
                <td className="py-2 pr-3">{fmtDate(campaign.grace_until)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      {drafting && (
        <DraftModal docType={selected} onClose={() => setDrafting(false)}
                    onSaved={async () => { setDrafting(false); await openFamily(selected); }} />
      )}
    </div>
  );
}

function DraftModal({ docType, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", body: "", effective_date: "",
                                     change_summary: "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await plPost(`/documents/${docType}/versions`, form);
      toast.success("Draft created.");
      onSaved();
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`New version: ${humanise(docType)}`} onClose={onClose}
           testId="pl-draft-modal"
           description="Drafts are editable. Once a version leaves draft its wording is frozen.">
      <Field label="Title">
        <input className={inputClass} value={form.title}
               onChange={(e) => setForm({ ...form, title: e.target.value })}
               data-testid="pl-draft-title" />
      </Field>
      <Field label="Wording">
        <textarea className={inputClass} rows={12} value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  data-testid="pl-draft-body" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Effective date">
          <input type="date" className={inputClass} value={form.effective_date}
                 onChange={(e) => setForm({ ...form, effective_date: e.target.value })} />
        </Field>
        <Field label="What changed">
          <input className={inputClass} value={form.change_summary}
                 onChange={(e) => setForm({ ...form, change_summary: e.target.value })} />
        </Field>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={save} busy={busy} data-testid="pl-draft-save">
          <FileText className="w-4 h-4" /> Create draft
        </Button>
      </div>
    </Modal>
  );
}
